/* audit-view.js — the v2 Audit view (Rewind Rail) on the createWorkbench
 * chassis. Grouped change tape with a reversibility rail, per-entry diff detail,
 * arm-to-rewind, server-side family/date filters + client-side text filter, CSV
 * export, and (local) checkpoint pins + Checkpoint button. Dual-mode via
 * audit-model.js; rail geometry via audit-rail.js. Never sends a `checkpoint`
 * selector in hosted mode (hidden hosted).
 */
import { esc, escAttr } from "./state.js";
import { iconHtml } from "./icons.js";
import { api } from "./api.js";
import { notify } from "./toast.js";
import { getAuthMode, getActiveConnection } from "./auth.js";
import { createWorkbench } from "./workbench.js";
import { normalizeAudit } from "./audit-model.js";
import { railViewModel, railCellHtml, pinRowHtml, parseDiffLines } from "./audit-rail.js";
import { renderAssistant, teardownAssistantDrawer } from "./assistant-drawer.js";
import { openUndoDialog } from "./rewind-dialog.js";

const _WINDOW = 60;
const _FAMILIES = ["approval_rule", "script", "custom_table", "attribute", "quote_custom_field",
  "template", "notification", "event_subscription"];

let _mount = null, _wb = null, _loadToken = 0;
let _mode = "local";
let _model = { groups: [], truncated: false };
let _items = [];                 // flattened render items: header / pin / entry
let _rail = { rows: [], pins: [], armSet: new Set(), interactive: true };
let _railById = new Map();
let _armedEntryId = null, _armCount = null;
let _filters = { family: "", since: "", until: "" };
let _query = "";
let _convMap = {};
let _detailDiff = null;
// windowing (database-view recipe)
let _visible = [], _shown = 0, _lastQuery = null, _lastLen = -1, _sentinelObserver = null;
let _refreshTimer = null;
let _escHandler = null;   // bound once per mount so remounts don't stack listeners

function _hosted() { return getAuthMode() === "hosted" && !!getActiveConnection(); }
function _isFiltered() {
  return !!(_filters.family || _filters.since || _filters.until || (_query && _query.trim()));
}

// ---- data load ------------------------------------------------------------
async function _load() {
  const myToken = ++_loadToken;
  const hosted = _hosted();
  _mode = hosted ? "hosted" : "local";
  if (hosted) {
    const cid = getActiveConnection();
    try {
      const cv = await api.connectionConversations(cid);
      _convMap = Object.fromEntries(((cv && cv.conversations) || []).map((c) => [c.id, c.title]));
    } catch { _convMap = {}; }
    if (myToken !== _loadToken) return;
  } else {
    _convMap = {};
  }
  const opts = hosted
    ? { since: _filters.since || undefined, until: _filters.until || undefined,
        family: _filters.family || undefined, limit: 200 }
    : { since: _filters.since || undefined, until: _filters.until || undefined,
        family: _filters.family || undefined };
  let raw;
  try { raw = await api.auditEntries(opts); }
  catch (e) {
    if (myToken !== _loadToken) return;
    _renderLoadError(e); return;
  }
  if (myToken !== _loadToken) return;
  _model = normalizeAudit(raw, _mode, { conversations: _convMap, limit: 200 });
  _rebuild();
}

function _renderLoadError(e) {
  if (!_mount) return;
  const list = _mount.querySelector(".wb-list");
  if (list) list.innerHTML = `<div class="wb-empty">Could not load the audit trail. `
    + `<button type="button" class="btn wb-retry">Retry</button></div>`;
  list?.querySelector(".wb-retry")?.addEventListener("click", () => _load());
}

// ---- flatten + rail -------------------------------------------------------
function _flatten() {
  _items = [];
  const pinsByGroup = new Map();
  for (const p of _rail.pins) {
    if (!pinsByGroup.has(p.groupId)) pinsByGroup.set(p.groupId, []);
    pinsByGroup.get(p.groupId).push(p);
  }
  for (const g of _model.groups) {
    _items.push({ kind: "header", id: "hdr:" + g.id, group: g });
    if (!_hosted()) for (const p of (pinsByGroup.get(g.id) || []))
      _items.push({ kind: "pin", id: "pin:" + g.id + ":" + p.label, pin: p });
    for (const e of g.entries) _items.push({ kind: "entry", id: e.id, entry: e, group: g });
  }
}

function _recomputeRail() {
  _rail = railViewModel(_model.groups, { armedEntryId: _armedEntryId, filtered: _isFiltered() });
  _railById = new Map(_rail.rows.map((r) => [r.id, r]));
}

function _rebuild() {
  _recomputeRail();
  // A reload can drop the armed anchor entirely (undone elsewhere, filtered
  // out, journal rewritten) or leave it dimmed; either way it is no longer a
  // valid rewind target, so drop the arm/confirm bar along with the tint.
  if (_armedEntryId != null) {
    const armedRow = _railById.get(_armedEntryId);
    if (!armedRow || armedRow.dimmed) _disarm(false);
  }
  _flatten();
  _lastQuery = null; _lastLen = -1;      // force onFilter to recompute the window
  if (_wb) _wb.setList(_items);
  _afterRender();
  _updateSub();
}

// Re-render only (arm-tint refresh) — reuses the same _items; renderRow reads
// the live _railById so armed tint updates without a reload.
function _rerender() {
  _recomputeRail();
  if (_wb) _wb.setList(_items);
  _afterRender();
}

// ---- row rendering --------------------------------------------------------
const _WORDS = { reversible: "Reversible", risky: "Risky", irreversible: "Record-only" };
function _revBadge(rev) {
  const map = { reversible: "rev-ok", risky: "rev-risk", irreversible: "rev-irrev" };
  return `<span class="rev-badge ${map[rev] || ""}"><span class="rev-dot"></span>${esc(_WORDS[rev] || rev)}</span>`;
}
function _fmtTime(ts) { return esc(String(ts || "").slice(0, 16).replace("T", " ")); }

function _rollupChips(roll) {
  const out = [];
  if (roll.reversible) out.push(`<span class="rev-badge rev-ok"><span class="rev-dot"></span>${roll.reversible} reversible</span>`);
  if (roll.risky) out.push(`<span class="rev-badge rev-risk"><span class="rev-dot"></span>${roll.risky} risky</span>`);
  if (roll.irreversible) out.push(`<span class="rev-badge rev-irrev"><span class="rev-dot"></span>${roll.irreversible} record-only</span>`);
  return out.join("");
}

function _headerHtml(g) {
  let undoBtn = "";
  if (g.kind === "task") undoBtn = `<button type="button" class="btn btn-xs v2a-undo-task" data-group="${escAttr(g.id)}">${iconHtml("undo", { size: "sm" })} Undo task</button>`;
  else if (g.kind === "run") undoBtn = `<button type="button" class="btn btn-xs v2a-undo-run" data-run="${escAttr(g.id)}">${iconHtml("undo", { size: "sm" })} Undo run</button>`;
  // kind "undo": no button (plan always empty — double-undo guard). kind "day": no button.
  return `<div class="v2a-group" data-group="${escAttr(g.id)}">`
    + `<span class="v2a-g-title">${esc(g.title)}</span>`
    + `<span class="v2a-g-count">${g.entries.length}</span>`
    + `<span class="v2a-g-roll">${_rollupChips(g.rollup)}</span>`
    + `<span class="v2a-g-ts">${_fmtTime(g.ts)}</span>`
    + `<span class="v2a-g-sp">${undoBtn}</span></div>`;
}

function _entryRowHtml(item) {
  const e = item.entry;
  const rr = _railById.get(e.id) || { color: "success", brk: false, dimmed: false, armable: false, armed: false };
  const armHint = rr.armable ? `<button type="button" class="btn-icon v2a-arm" data-id="${escAttr(e.id)}" title="Rewind to here">${iconHtml("rewind", { size: "sm" })}</button>` : "";
  const diffBtn = e.has_diff ? `<button type="button" class="btn-icon v2a-diff" data-id="${escAttr(e.id)}" title="View diff">${iconHtml("diff", { size: "sm" })}</button>` : "";
  const undoOne = rr.dimmed ? "" : `<button type="button" class="btn-icon v2a-undo-one" data-id="${escAttr(e.id)}" title="Undo this change">${iconHtml("undo", { size: "sm" })}</button>`;
  return `<div class="wb-row v2a-row${rr.dimmed ? " v2a-row-dim" : ""}${rr.armed ? " v2a-row-armed" : ""}" data-id="${escAttr(e.id)}" data-seg="all">`
    + railCellHtml(rr)
    + `<span class="v2a-op v2a-op-${escAttr((e.op || "").toLowerCase())}">${esc(e.op || "")}</span>`
    + `<span class="v2a-target">${esc(e.targetLabel || e.family || "")}</span>`
    + `<span class="v2a-tool">${esc(e.tool || "")}</span>`
    + _revBadge(e.reversibility)
    + `<span class="v2a-ts">${_fmtTime(e.ts)}</span>`
    + `<span class="v2a-row-actions">${armHint}${diffBtn}${undoOne}</span></div>`;
}

function _renderRow(item) {
  if (item.kind === "header") return _headerHtml(item.group);
  if (item.kind === "pin") return pinRowHtml(item.pin);
  return _entryRowHtml(item);
}

// ---- windowing (database-view recipe) -------------------------------------
function _onFilter(items, query) {
  const wasFiltered = _isFiltered();
  _query = query || "";
  if (query !== _lastQuery || items.length !== _lastLen) {
    _lastQuery = query; _lastLen = items.length;
    const q = (query || "").trim().toLowerCase();
    if (!q) _visible = items.slice();
    else {
      // Client-side text filter over the loaded set: keep entries matching the
      // query, and any header/pin whose group has at least one match. Headers
      // carry the group on `.group`; pins carry it on `.pin.groupId`
      // (pinRowHtml/railViewModel's pin shape has no `.group`).
      const hitGroups = new Set();
      for (const it of items) if (it.kind === "entry"
        && ((it.entry.targetLabel || "") + " " + (it.entry.tool || "") + " " + (it.entry.op || "")).toLowerCase().includes(q))
        hitGroups.add(it.group.id);
      _visible = items.filter((it) => {
        if (it.kind === "entry")
          return ((it.entry.targetLabel || "") + " " + (it.entry.tool || "") + " " + (it.entry.op || "")).toLowerCase().includes(q);
        const gid = it.kind === "pin" ? it.pin.groupId : (it.group && it.group.id);
        return gid != null && hitGroups.has(gid);
      });
    }
    _shown = Math.min(_WINDOW, _visible.length);
    // A text filter degrades the rail (honesty contract) — disarm, and on an
    // active/inactive transition recompute the rail so armable rows go
    // non-interactive (or restore) before this same render pass paints them.
    if (q && _armedEntryId) _disarm(false);
    if (_isFiltered() !== wasFiltered) _recomputeRail();
  }
  return _visible.slice(0, _shown);
}

function _afterRender() {
  if (!_mount) return;
  const list = _mount.querySelector(".wb-list");
  if (!list) return;
  if (_shown < _visible.length) {
    list.insertAdjacentHTML("beforeend", `<div class="v2a-sentinel" aria-hidden="true" style="height:1px"></div>`);
    _observeSentinel(list.querySelector(".v2a-sentinel"));
  }
  _renderConfirmBar();
}

function _observeSentinel(sentinel) {
  if (!sentinel) return;
  if (!_sentinelObserver) {
    const root = (_mount && _mount.querySelector(".wb-list")) || null;
    _sentinelObserver = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) _growWindow();
    }, { root, rootMargin: "400px 0px" });
  }
  _sentinelObserver.observe(sentinel);
}
function _growWindow() {
  if (_shown >= _visible.length) return;
  _shown = Math.min(_shown + _WINDOW, _visible.length);
  if (_wb) _wb.setList(_items);
  _afterRender();
}

// ---- arm flow -------------------------------------------------------------
async function _arm(entryId) {
  if (_isFiltered()) return;                 // honesty contract: no arming on filtered tape
  const rr = _railById.get(entryId);
  if (!rr || !rr.armable) return;
  _armedEntryId = entryId; _armCount = null;
  _rerender();                               // tint everything newer
  try {
    const plan = await api.undoPreview({ upto_entry_id: entryId }, getActiveConnection() || undefined);
    if (_armedEntryId !== entryId) return;   // disarmed mid-flight
    _armCount = (plan && plan.count) != null ? plan.count : (plan && plan.steps ? plan.steps.length : null);
  } catch { _armCount = null; }
  _renderConfirmBar();
}

function _disarm(rerender = true) {
  _armedEntryId = null; _armCount = null;
  if (rerender) _rerender(); else _renderConfirmBar();
}

function _renderConfirmBar() {
  if (!_mount) return;
  let bar = _mount.querySelector(".v2a-confirm");
  if (_armedEntryId == null) { if (bar) bar.remove(); return; }
  const n = _armCount != null ? _armCount : "…";
  if (!bar) {
    bar = document.createElement("div");
    bar.className = "v2a-confirm";
    _mount.querySelector(".wb-rail")?.appendChild(bar);
  }
  bar.innerHTML = `<span class="v2a-confirm-txt">Rewind <b>${esc(String(n))}</b> change${n === 1 ? "" : "s"} to here</span>`
    + `<button type="button" class="btn btn-xs v2a-confirm-go">Confirm</button>`
    + `<button type="button" class="btn btn-xs v2a-confirm-x">Cancel</button>`;
  bar.querySelector(".v2a-confirm-go").addEventListener("click", () => {
    const conn = null; // openUndoDialog fetches active-connection meta when conn is null
    openUndoDialog({ upto_entry_id: _armedEntryId }, () => { _disarm(false); _load(); }, conn);
  });
  bar.querySelector(".v2a-confirm-x").addEventListener("click", () => _disarm());
}

// ---- detail pane ----------------------------------------------------------
async function _openEntry(entry, detailApi) {
  _detailDiff = null;
  detailApi.setHead(_detailHeadHtml(entry));
  _wireDetailHead(entry);
  const body = document.createElement("div");
  body.className = "v2a-detail-body";
  body.innerHTML = `<div class="wb-code-loading code-placeholder">Fetching…</div>`;
  detailApi.setBody(body);
  if (!entry.has_diff) { body.innerHTML = `<div class="v2a-nodiff">No before/after captured for this change.</div>`; return; }
  try {
    const data = await api.auditEntry(entry.id);
    _detailDiff = data && data.diff ? data.diff : "";
    const rows = parseDiffLines(_detailDiff);
    body.innerHTML = rows.length
      ? `<div class="wb-diff">${rows.map((r) => `<div class="${r.cls}">${esc(r.text)}</div>`).join("")}</div>`
      : `<div class="v2a-nodiff">No differences to show.</div>`;
  } catch (e) {
    body.innerHTML = `<div class="v2a-nodiff" style="color:var(--error)">Could not load the diff. `
      + `<button type="button" class="btn wb-retry">Retry</button></div>`;
    body.querySelector(".wb-retry")?.addEventListener("click", () => _openEntry(entry, detailApi));
  }
}

function _detailHeadHtml(e) {
  const status = (e.reverse_status && e.reverse_status !== "pending")
    ? `<span class="v2a-det-status">${esc(e.reverse_status)}</span>` : "";
  const undoOne = (e.reverse_status === "reversed" || e.reverse_of) ? "" :
    `<button type="button" class="btn btn-xs v2a-det-undo">${iconHtml("undo", { size: "sm" })} Undo</button>`;
  return `<span class="v2a-op v2a-op-${escAttr((e.op || "").toLowerCase())}">${esc(e.op || "")}</span>`
    + `<span class="wb-name">${esc(e.targetLabel || e.family || "")}</span>`
    + `<span class="v2a-det-tool">${esc(e.tool || "")}</span>`
    + `<span class="v2a-det-ts">${_fmtTime(e.ts)}</span>`
    + _revBadge(e.reversibility) + status
    + `<span class="wb-det-actions"><button type="button" class="btn btn-xs v2a-det-ask">${iconHtml("sparkles", { size: "sm" })} Ask AI</button>${undoOne}</span>`;
}

function _wireDetailHead(e) {
  const head = _mount.querySelector(".wb-detail-head");
  if (!head) return;
  head.querySelector(".v2a-det-undo")?.addEventListener("click", () => {
    openUndoDialog({ entry_ids: [e.id] }, () => _load(), null);
  });
  head.querySelector(".v2a-det-ask")?.addEventListener("click", () => {
    // Carry the ALREADY-loaded diff so the drawer's chip clicks are synchronous.
    _wb.openDrawer({ kind: "audit", label: e.targetLabel || e.family || "change",
      entry: { ...e, diff: _detailDiff || "" } });
  });
}

// ---- delegated list actions (survive innerHTML rebuilds) ------------------
function _onListClick(ev) {
  const arm = ev.target.closest(".v2a-arm");
  if (arm) { ev.stopPropagation(); _arm(arm.getAttribute("data-id")); return; }
  const diff = ev.target.closest(".v2a-diff");
  if (diff) { ev.stopPropagation(); _wb.select(diff.getAttribute("data-id")); return; }
  const undoOne = ev.target.closest(".v2a-undo-one");
  if (undoOne) { ev.stopPropagation(); openUndoDialog({ entry_ids: [undoOne.getAttribute("data-id")] }, () => _load(), null); return; }
  const undoTask = ev.target.closest(".v2a-undo-task");
  if (undoTask) { ev.stopPropagation(); openUndoDialog({ group_id: undoTask.getAttribute("data-group") }, () => _load(), null); return; }
  const undoRun = ev.target.closest(".v2a-undo-run");
  if (undoRun) {
    ev.stopPropagation();
    // Local "run" groups are keyed by their group_id (already the
    // "promotion:<run_id>" string the local rewind tools match on); a bare
    // run_id is not a param those tools declare, so it would silently drop
    // and preview/undo the whole journal. Hosted has a real run_id selector.
    const id = undoRun.getAttribute("data-run");
    openUndoDialog(_hosted() ? { run_id: id } : { group_id: id }, () => _load(), null);
    return;
  }
  const pin = ev.target.closest(".v2a-pinrow");
  if (pin && !_hosted()) { ev.stopPropagation(); openUndoDialog({ checkpoint: pin.getAttribute("data-checkpoint") }, () => _load(), null); return; }
}

// ---- header actions -------------------------------------------------------
function _headerActions() {
  const wrap = document.createElement("div");
  wrap.className = "v2a-actions";
  const famOpts = ['<option value="">All families</option>']
    .concat(_FAMILIES.map((f) => `<option value="${escAttr(f)}">${esc(f.replace(/_/g, " "))}</option>`)).join("");
  wrap.innerHTML =
    `<select class="v2a-family">${famOpts}</select>`
    + `<input type="date" class="v2a-since" title="Since" />`
    + `<input type="date" class="v2a-until" title="Until" />`
    + `<button type="button" class="btn v2a-csv">${iconHtml("download", { size: "sm" })} CSV</button>`
    + (_hosted() ? "" : `<button type="button" class="btn v2a-checkpoint">${iconHtml("pin", { size: "sm" })} Checkpoint</button>`);
  wrap.querySelector(".v2a-family").addEventListener("change", (e) => { _filters.family = e.target.value; _disarm(false); _load(); });
  wrap.querySelector(".v2a-since").addEventListener("change", (e) => { _filters.since = e.target.value; _disarm(false); _load(); });
  wrap.querySelector(".v2a-until").addEventListener("change", (e) => { _filters.until = e.target.value; _disarm(false); _load(); });
  wrap.querySelector(".v2a-csv").addEventListener("click", async () => {
    try { await api.downloadAuditCsv({ since: _filters.since || undefined, until: _filters.until || undefined, family: _filters.family || undefined }); }
    catch (err) { notify(err.message || "Export CSV failed.", "error"); }
  });
  wrap.querySelector(".v2a-checkpoint")?.addEventListener("click", async () => {
    const label = (window.prompt("Checkpoint label", "before-change") || "").trim();
    if (!label) return;
    try { await api.checkpointCreate(label); notify("Checkpoint saved.", "info"); _load(); }
    catch (err) { notify(err.message || "Checkpoint failed.", "error"); }
  });
  return wrap;
}

function _updateSub() {
  const sub = _mount && _mount.querySelector(".content-head .ch-sub");
  if (!sub) return;
  const n = _model.groups.reduce((a, g) => a + g.entries.length, 0);
  sub.textContent = _model.truncated ? `Showing latest ${n}` : `${n} change${n === 1 ? "" : "s"}`;
}

// ---- mount / reset / refresh ----------------------------------------------
export async function renderAuditInto(container) {
  if (!container) return;
  _mount = container;
  _armedEntryId = null; _armCount = null;
  _filters = { family: "", since: "", until: "" }; _query = "";
  _wb = createWorkbench(container, {
    header: { title: "Audit", sub: "", actions: _headerActions() },
    onFilter: _onFilter,
    renderRow: _renderRow,
    onSelect: (item, detailApi) => { if (item && item.kind === "entry") _openEntry(item.entry, detailApi); },
    drawer: { title: "Assistant", render: (ctx, body) => renderAssistant(ctx, body) },
  });
  // Capture phase: workbench's own row-select click listener on this same
  // element runs in the bubble phase (workbench.js), so a bubble-phase
  // listener here would fire AFTER it, letting an in-row action click (arm/
  // diff/undo-one) also trigger row selection. Running in capture lets our
  // stopPropagation() (below, action branches only) pre-empt it; a plain row
  // click still falls through untouched, so selection keeps working.
  const list = container.querySelector(".wb-list");
  if (list) list.addEventListener("click", _onListClick, true);
  const filterInput = container.querySelector(".wb-filter-input");
  if (filterInput) filterInput.addEventListener("input", _afterRender);
  // Esc disarms (in addition to workbench's own mount-level Esc). Bound once
  // per mount and removed in resetAuditView -- #v2AuditMount persists across
  // tenant/connection switches, so leaving this attached would stack one more
  // listener on the same element every remount.
  _escHandler = (e) => { if (e.key === "Escape" && _armedEntryId != null) _disarm(); };
  container.addEventListener("keydown", _escHandler);
  await _load();
}

export function resetAuditView() {
  teardownAssistantDrawer();
  if (_sentinelObserver) { _sentinelObserver.disconnect(); _sentinelObserver = null; }
  if (_refreshTimer) { clearTimeout(_refreshTimer); _refreshTimer = null; }
  if (_mount && _escHandler) _mount.removeEventListener("keydown", _escHandler);
  _escHandler = null;
  _wb = null; _mount = null;
  _model = { groups: [], truncated: false };
  _items = []; _rail = { rows: [], pins: [], armSet: new Set(), interactive: true }; _railById = new Map();
  _armedEntryId = null; _armCount = null;
  _filters = { family: "", since: "", until: "" }; _query = "";
  _convMap = {}; _detailDiff = null;
  _visible = []; _shown = 0; _lastQuery = null; _lastLen = -1;
  _loadToken++;
}

export function refreshAuditView() {
  if (!_mount || !_wb) return;               // no-op until mounted
  clearTimeout(_refreshTimer);
  _refreshTimer = setTimeout(() => { _load(); }, 200);
}

// ---- E2E seam -------------------------------------------------------------
if (typeof window !== "undefined") {
  window.__test_audit = {
    setData(raw, mode, opts = {}) {
      _mode = mode || (_hosted() ? "hosted" : "local");
      _convMap = opts.conversations || {};
      _model = normalizeAudit(raw, _mode, { conversations: _convMap, limit: opts.limit != null ? opts.limit : 200 });
      _rebuild();
    },
    refresh() { return _load(); },
  };
}
